<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCommercialReports } from '../composables/useCommercialReports';
import CommercialPipelineTable from '../components/CommercialPipelineTable.vue';
import CommercialIntelligenceTable from '../components/CommercialIntelligenceTable.vue';
import CommercialReportChart from '../components/CommercialReportChart.vue';
import CommercialReportTable from '../components/CommercialReportTable.vue';

const { t, locale } = useI18n();
const { report, filters, isLoading, error, fetchReport, clearFilters } =
  useCommercialReports();

const money = value =>
  new Intl.NumberFormat(locale.value, {
    style: 'currency',
    currency: report.value?.currency || 'COP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
const number = value => new Intl.NumberFormat(locale.value).format(value || 0);
const dateTime = value =>
  value
    ? new Intl.DateTimeFormat(locale.value, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value))
    : '—';

const kpis = computed(() => {
  const summary = report.value?.summary || {};
  return [
    {
      label: t('COMMERCIAL.REPORTS.KPI.OPEN_COUNT'),
      value: number(summary.open_count),
      icon: 'i-lucide-briefcase-business',
      iconClass: 'bg-n-blue-3 text-n-blue-11',
      accentClass: 'bg-n-blue-9',
    },
    {
      label: t('COMMERCIAL.REPORTS.KPI.WON_COUNT'),
      value: number(summary.won_count),
      icon: 'i-lucide-trophy',
      iconClass: 'bg-n-teal-3 text-n-teal-11',
      accentClass: 'bg-n-teal-9',
    },
    {
      label: t('COMMERCIAL.REPORTS.KPI.LOST_COUNT'),
      value: number(summary.lost_count),
      icon: 'i-lucide-circle-x',
      iconClass: 'bg-n-ruby-3 text-n-ruby-11',
      accentClass: 'bg-n-ruby-9',
    },
    {
      label: t('COMMERCIAL.REPORTS.KPI.OPEN_VALUE'),
      value: money(summary.open_value),
      icon: 'i-lucide-chart-no-axes-combined',
      iconClass: 'bg-n-indigo-3 text-n-indigo-11',
      accentClass: 'bg-n-indigo-9',
    },
    {
      label: t('COMMERCIAL.REPORTS.KPI.WON_VALUE'),
      value: money(summary.won_value),
      icon: 'i-lucide-badge-dollar-sign',
      iconClass: 'bg-n-teal-3 text-n-teal-11',
      accentClass: 'bg-n-teal-9',
    },
    {
      label: t('COMMERCIAL.REPORTS.KPI.CLOSE_RATE'),
      value: `${summary.close_rate || 0}%`,
      icon: 'i-lucide-gauge',
      iconClass: 'bg-n-amber-3 text-n-amber-11',
      accentClass: 'bg-n-amber-9',
    },
    {
      label: t('COMMERCIAL.REPORTS.KPI.AVERAGE_TICKET'),
      value: money(summary.average_ticket),
      icon: 'i-lucide-receipt-text',
      iconClass: 'bg-n-violet-3 text-n-violet-11',
      accentClass: 'bg-n-violet-9',
    },
    {
      label: t('COMMERCIAL.REPORTS.KPI.LOST_VALUE'),
      value: money(summary.lost_value),
      icon: 'i-lucide-trending-down',
      iconClass: 'bg-n-ruby-3 text-n-ruby-11',
      accentClass: 'bg-n-ruby-9',
    },
  ];
});

const appliedFilters = computed(() => {
  if (!report.value) return [];
  const options = report.value.filters;
  return [
    filters.dateFrom &&
      `${t('COMMERCIAL.REPORTS.FILTERS.FROM')}: ${filters.dateFrom}`,
    filters.dateTo &&
      `${t('COMMERCIAL.REPORTS.FILTERS.TO')}: ${filters.dateTo}`,
    options.agents.find(
      item => String(item.id) === String(filters.assignedUserId)
    )?.name,
    options.companies.find(
      item => String(item.id) === String(filters.companyId)
    )?.name,
    options.stages.find(item => String(item.id) === String(filters.stageId))
      ?.name,
    filters.status &&
      {
        open: t('COMMERCIAL.STATUS.OPEN'),
        won: t('COMMERCIAL.STATUS.WON'),
        lost: t('COMMERCIAL.STATUS.LOST'),
      }[filters.status],
  ].filter(Boolean);
});

const pipelineValueChart = computed(() => ({
  labels: report.value.pipeline.stages.map(stage => stage.name),
  datasets: [
    {
      data: report.value.pipeline.stages.map(stage => Number(stage.value)),
      backgroundColor: report.value.pipeline.stages.map(
        stage => stage.color || '#2563EB'
      ),
      borderRadius: 6,
      maxBarThickness: 44,
    },
  ],
}));
const pipelineCountChart = computed(() => ({
  labels: report.value.pipeline.stages.map(stage => stage.name),
  datasets: [
    {
      data: report.value.pipeline.stages.map(stage => stage.count),
      backgroundColor: '#6366F1',
      borderRadius: 6,
      maxBarThickness: 44,
    },
  ],
}));
const statusChart = computed(() => ({
  labels: [
    t('COMMERCIAL.STATUS.OPEN'),
    t('COMMERCIAL.STATUS.WON'),
    t('COMMERCIAL.STATUS.LOST'),
  ],
  datasets: [
    {
      data: [
        report.value.summary.open_count,
        report.value.summary.won_count,
        report.value.summary.lost_count,
      ],
      backgroundColor: ['#3B82F6', '#14B8A6', '#EF4444'],
      borderWidth: 0,
      hoverOffset: 4,
    },
  ],
}));
const lossesChart = computed(() => ({
  labels: report.value.losses.by_reason.map(
    row => row.name || t('COMMERCIAL.REPORTS.NO_REASON')
  ),
  datasets: [
    {
      data: report.value.losses.by_reason.map(row => Number(row.value)),
      backgroundColor: '#F43F5E',
      borderRadius: 6,
      maxBarThickness: 34,
    },
  ],
}));

const csvCell = value => `"${String(value ?? '').replaceAll('"', '""')}"`;
const exportCsv = () => {
  const sections = [
    [
      t('COMMERCIAL.REPORTS.PIPELINE'),
      [
        t('COMMERCIAL.REPORTS.COLUMNS.STAGE'),
        t('COMMERCIAL.REPORTS.COLUMNS.COUNT'),
        t('COMMERCIAL.REPORTS.COLUMNS.VALUE'),
      ],
      report.value.pipeline.stages.map(row => [row.name, row.count, row.value]),
    ],
    [
      t('COMMERCIAL.REPORTS.LOSSES'),
      [
        t('COMMERCIAL.REPORTS.COLUMNS.REASON'),
        t('COMMERCIAL.REPORTS.COLUMNS.COUNT'),
        t('COMMERCIAL.REPORTS.COLUMNS.VALUE'),
      ],
      report.value.losses.by_reason.map(row => [
        row.name || t('COMMERCIAL.REPORTS.NO_REASON'),
        row.count,
        row.value,
      ]),
    ],
  ];
  if (report.value.intelligence) {
    sections.push(
      [
        t('COMMERCIAL.REPORTS.INTELLIGENCE.NEXT_BEST_ACTIONS'),
        [
          t('COMMERCIAL.REPORTS.INTELLIGENCE.PRIORITY'),
          t('COMMERCIAL.REPORTS.INTELLIGENCE.DEAL'),
          t('COMMERCIAL.REPORTS.COLUMNS.STAGE'),
          t('COMMERCIAL.REPORTS.INTELLIGENCE.ADVISOR'),
          t('COMMERCIAL.REPORTS.INTELLIGENCE.RECOMMENDED_ACTION'),
          t('COMMERCIAL.REPORTS.INTELLIGENCE.REASON'),
        ],
        report.value.intelligence.next_best_actions.map(row => [
          row.priority,
          row.deal_name,
          row.stage_name,
          row.assigned_agent_name,
          row.recommended_action,
          row.reason,
        ]),
      ],
      [
        t('COMMERCIAL.REPORTS.INTELLIGENCE.OPPORTUNITIES_AT_RISK'),
        [
          t('COMMERCIAL.REPORTS.INTELLIGENCE.RISK'),
          t('COMMERCIAL.REPORTS.INTELLIGENCE.DEAL'),
          t('COMMERCIAL.REPORTS.INTELLIGENCE.INACTIVE_DAYS'),
          t('COMMERCIAL.REPORTS.INTELLIGENCE.OVERDUE'),
          t('COMMERCIAL.REPORTS.INTELLIGENCE.RECOMMENDED_ACTION'),
        ],
        report.value.intelligence.opportunities_at_risk.map(row => [
          row.risk_level,
          row.name,
          row.days_without_activity,
          row.overdue_activities_count,
          row.recommended_action,
        ]),
      ],
      [
        t('COMMERCIAL.REPORTS.INTELLIGENCE.HOT_OPPORTUNITIES'),
        [
          t('COMMERCIAL.REPORTS.INTELLIGENCE.SCORE'),
          t('COMMERCIAL.REPORTS.INTELLIGENCE.DEAL'),
          t('COMMERCIAL.REPORTS.COLUMNS.VALUE'),
          t('COMMERCIAL.REPORTS.COLUMNS.STAGE'),
          t('COMMERCIAL.REPORTS.INTELLIGENCE.RECOMMENDED_ACTION'),
        ],
        report.value.intelligence.hot_opportunities.map(row => [
          row.score,
          row.name,
          row.value,
          row.stage_name,
          row.recommended_action,
        ]),
      ]
    );
  }
  const csv = sections
    .flatMap(([title, headers, rows]) => [[title], headers, ...rows, []])
    .map(row => row.map(csvCell).join(','))
    .join('\n');
  const url = URL.createObjectURL(
    new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' })
  );
  const link = document.createElement('a');
  link.href = url;
  link.download = `commercial-report-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};
const printReport = () => window.print();

onMounted(fetchReport);
</script>

<template>
  <main
    class="h-full overflow-y-auto bg-n-background text-n-slate-12 print:h-auto print:overflow-visible print:bg-white print:text-slate-950"
  >
    <div
      class="mx-auto max-w-[1600px] p-4 md:p-6 lg:p-8 print:max-w-none print:p-0"
    >
      <header class="mb-6 break-inside-avoid">
        <div
          class="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between"
        >
          <div class="flex min-w-0 items-start gap-3">
            <span
              class="grid size-11 shrink-0 place-content-center rounded-xl bg-n-brand/10 text-n-brand print:hidden"
            >
              <span class="i-lucide-chart-spline size-5" />
            </span>
            <div class="min-w-0">
              <h1 class="text-2xl font-semibold tracking-tight md:text-3xl">
                {{ t('COMMERCIAL.REPORTS.TITLE') }}
              </h1>
              <p
                class="mt-1 max-w-2xl text-sm text-n-slate-10 md:text-base print:text-slate-600"
              >
                {{ t('COMMERCIAL.REPORTS.SUBTITLE') }}
              </p>
              <p
                v-if="report"
                class="mt-2 text-xs text-n-slate-9 print:text-slate-500"
              >
                {{
                  t('COMMERCIAL.REPORTS.PRINTED_AT', {
                    date: dateTime(report.generated_at),
                  })
                }}
              </p>
            </div>
          </div>
          <div class="flex w-full gap-2 sm:w-auto print:hidden">
            <button
              class="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-n-weak bg-n-solid-2 px-4 py-2.5 text-sm font-medium hover:bg-n-alpha-2 disabled:opacity-50 sm:flex-none"
              :disabled="!report"
              @click="exportCsv"
            >
              <span class="i-lucide-download size-4" />
              {{ t('COMMERCIAL.REPORTS.ACTIONS.EXPORT') }}
            </button>
            <button
              class="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-n-brand px-4 py-2.5 text-sm font-medium text-white hover:bg-n-brand-hover sm:flex-none"
              @click="printReport"
            >
              <span class="i-lucide-printer size-4" />
              {{ t('COMMERCIAL.REPORTS.ACTIONS.PRINT') }}
            </button>
          </div>
        </div>

        <div
          v-if="report"
          class="mt-4 flex flex-wrap items-center gap-2 text-xs"
        >
          <span class="font-medium text-n-slate-10">{{
            t('COMMERCIAL.REPORTS.FILTERS.APPLIED')
          }}</span>
          <template v-if="appliedFilters.length">
            <span
              v-for="filter in appliedFilters"
              :key="filter"
              class="rounded-full border border-n-weak bg-n-alpha-2 px-2.5 py-1 text-n-slate-11 print:border-slate-300 print:bg-white print:text-slate-700"
            >
              {{ filter }}
            </span>
          </template>
          <span
            v-else
            class="rounded-full bg-n-alpha-2 px-2.5 py-1 text-n-slate-10 print:bg-white"
          >
            {{ t('COMMERCIAL.REPORTS.FILTERS.NONE') }}
          </span>
        </div>
      </header>

      <form
        class="mb-7 grid grid-cols-1 gap-3 rounded-2xl border border-n-weak bg-n-solid-2 p-4 shadow-sm sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-7 print:hidden"
        @submit.prevent="fetchReport"
      >
        <label class="text-xs font-medium text-n-slate-10">
          {{ t('COMMERCIAL.REPORTS.FILTERS.FROM') }}
          <input
            v-model="filters.dateFrom"
            type="date"
            class="mt-1.5 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          />
        </label>
        <label class="text-xs font-medium text-n-slate-10">
          {{ t('COMMERCIAL.REPORTS.FILTERS.TO') }}
          <input
            v-model="filters.dateTo"
            type="date"
            class="mt-1.5 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          />
        </label>
        <label class="text-xs font-medium text-n-slate-10">
          {{ t('COMMERCIAL.REPORTS.FILTERS.ADVISOR') }}
          <select
            v-model="filters.assignedUserId"
            class="mt-1.5 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option value="">{{ t('COMMERCIAL.REPORTS.FILTERS.ALL') }}</option>
            <option
              v-for="item in report?.filters.agents || []"
              :key="item.id"
              :value="item.id"
            >
              {{ item.name }}
            </option>
          </select>
        </label>
        <label class="text-xs font-medium text-n-slate-10">
          {{ t('COMMERCIAL.REPORTS.FILTERS.COMPANY') }}
          <select
            v-model="filters.companyId"
            class="mt-1.5 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option value="">{{ t('COMMERCIAL.REPORTS.FILTERS.ALL') }}</option>
            <option
              v-for="item in report?.filters.companies || []"
              :key="item.id"
              :value="item.id"
            >
              {{ item.name }}
            </option>
          </select>
        </label>
        <label class="text-xs font-medium text-n-slate-10">
          {{ t('COMMERCIAL.REPORTS.FILTERS.STAGE') }}
          <select
            v-model="filters.stageId"
            class="mt-1.5 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option value="">{{ t('COMMERCIAL.REPORTS.FILTERS.ALL') }}</option>
            <option
              v-for="item in report?.filters.stages || []"
              :key="item.id"
              :value="item.id"
            >
              {{ item.name }}
            </option>
          </select>
        </label>
        <label class="text-xs font-medium text-n-slate-10">
          {{ t('COMMERCIAL.REPORTS.FILTERS.STATUS') }}
          <select
            v-model="filters.status"
            class="mt-1.5 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2.5 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option value="">{{ t('COMMERCIAL.REPORTS.FILTERS.ALL') }}</option>
            <option value="open">{{ t('COMMERCIAL.STATUS.OPEN') }}</option>
            <option value="won">{{ t('COMMERCIAL.STATUS.WON') }}</option>
            <option value="lost">{{ t('COMMERCIAL.STATUS.LOST') }}</option>
          </select>
        </label>
        <div class="flex items-end gap-2">
          <button
            type="submit"
            class="flex-1 rounded-lg bg-n-brand px-3 py-2.5 text-sm font-medium text-white hover:bg-n-brand-hover"
          >
            {{ t('COMMERCIAL.REPORTS.ACTIONS.APPLY') }}
          </button>
          <button
            type="button"
            class="grid size-10 shrink-0 place-content-center rounded-lg border border-n-weak text-n-slate-10 hover:bg-n-alpha-2"
            :title="t('COMMERCIAL.REPORTS.ACTIONS.CLEAR')"
            @click="clearFilters"
          >
            <span class="i-lucide-rotate-ccw size-4" />
          </button>
        </div>
      </form>

      <div
        v-if="isLoading"
        class="flex min-h-96 flex-col items-center justify-center gap-3 text-n-slate-10"
      >
        <span
          class="i-lucide-loader-circle size-7 animate-spin text-n-brand"
        />{{ t('COMMERCIAL.REPORTS.LOADING') }}
      </div>
      <div
        v-else-if="error"
        class="rounded-2xl border border-n-ruby-6 bg-n-ruby-3 p-6 text-n-ruby-11"
      >
        <span
          class="i-lucide-circle-alert me-2 inline-block size-5 align-middle"
        />{{ t('COMMERCIAL.REPORTS.ERROR') }}
        <button class="font-medium underline" @click="fetchReport">
          {{ t('COMMERCIAL.REPORTS.ACTIONS.RETRY') }}
        </button>
      </div>

      <div v-else-if="report" class="space-y-8">
        <section class="break-inside-avoid">
          <div class="mb-3 flex items-end justify-between">
            <div>
              <p
                class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
              >
                {{ t('COMMERCIAL.REPORTS.OVERVIEW') }}
              </p>
              <h2 class="mt-1 text-lg font-semibold">
                {{ t('COMMERCIAL.REPORTS.SUMMARY') }}
              </h2>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-3 lg:grid-cols-4 print:grid-cols-4">
            <article
              v-for="kpi in kpis"
              :key="kpi.label"
              class="relative overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 p-4 shadow-sm print:border-slate-300 print:bg-white print:shadow-none"
            >
              <span
                class="absolute inset-x-0 top-0 h-0.5"
                :class="kpi.accentClass"
              />
              <div class="flex items-start justify-between gap-2">
                <div class="min-w-0">
                  <p
                    class="text-xs font-medium text-n-slate-10 print:text-slate-600"
                  >
                    {{ kpi.label }}
                  </p>
                  <p
                    class="mt-2 truncate text-xl font-semibold tracking-tight text-n-slate-12 md:text-2xl print:text-slate-950"
                  >
                    {{ kpi.value }}
                  </p>
                </div>
                <span
                  class="grid size-9 shrink-0 place-content-center rounded-xl"
                  :class="kpi.iconClass"
                >
                  <span class="size-4" :class="kpi.icon" />
                </span>
              </div>
            </article>
          </div>
        </section>

        <section class="break-inside-avoid">
          <div class="mb-3">
            <p
              class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
            >
              {{ t('COMMERCIAL.REPORTS.PRIORITY') }}
            </p>
            <h2 class="mt-1 text-lg font-semibold">
              {{ t('COMMERCIAL.REPORTS.ACTIVITIES') }}
            </h2>
          </div>
          <div class="grid gap-3 md:grid-cols-2">
            <article
              class="flex items-center gap-4 rounded-2xl border border-n-amber-6 bg-n-amber-3 p-5 print:border-amber-300 print:bg-amber-50"
            >
              <span
                class="grid size-11 shrink-0 place-content-center rounded-xl bg-n-amber-5 text-n-amber-11"
              >
                <span class="i-lucide-clock-alert size-5" />
              </span>
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium text-n-amber-11">
                  {{ t('COMMERCIAL.REPORTS.NO_ACTIVITY') }}
                </p>
                <p class="mt-0.5 text-xs text-n-amber-10">
                  {{ t('COMMERCIAL.REPORTS.NO_ACTIVITY_HINT') }}
                </p>
              </div>
              <strong class="text-3xl tabular-nums text-n-amber-12">{{
                number(report.activities.no_activity_count)
              }}</strong>
            </article>
            <article
              class="flex items-center gap-4 rounded-2xl border border-n-ruby-6 bg-n-ruby-3 p-5 print:border-rose-300 print:bg-rose-50"
            >
              <span
                class="grid size-11 shrink-0 place-content-center rounded-xl bg-n-ruby-5 text-n-ruby-11"
              >
                <span class="i-lucide-calendar-x-2 size-5" />
              </span>
              <div class="min-w-0 flex-1">
                <p class="text-sm font-medium text-n-ruby-11">
                  {{ t('COMMERCIAL.REPORTS.OVERDUE_ACTIVITY') }}
                </p>
                <p class="mt-0.5 text-xs text-n-ruby-10">
                  {{ t('COMMERCIAL.REPORTS.OVERDUE_ACTIVITY_HINT') }}
                </p>
              </div>
              <strong class="text-3xl tabular-nums text-n-ruby-12">{{
                number(report.activities.overdue_deals_count)
              }}</strong>
            </article>
          </div>
          <div
            v-if="
              !report.activities.no_activity_count &&
              !report.activities.overdue_deals_count
            "
            class="mt-3 flex items-center gap-3 rounded-xl border border-n-teal-6 bg-n-teal-3 p-4 text-sm text-n-teal-11"
          >
            <span class="i-lucide-circle-check-big size-5" />{{
              t('COMMERCIAL.REPORTS.ACTIVITIES_CLEAR')
            }}
          </div>
        </section>

        <section v-if="report.intelligence" class="space-y-5">
          <div
            class="break-inside-avoid rounded-2xl border border-n-violet-6 bg-gradient-to-br from-n-violet-3 to-n-solid-2 p-5 print:border-slate-300 print:bg-white"
          >
            <div class="flex items-start gap-3">
              <span
                class="grid size-10 shrink-0 place-content-center rounded-xl bg-n-violet-5 text-n-violet-11"
              >
                <span class="i-lucide-sparkles size-5" />
              </span>
              <div>
                <p
                  class="text-xs font-medium uppercase tracking-wide text-n-violet-10"
                >
                  {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.DETERMINISTIC') }}
                </p>
                <h2 class="mt-1 text-xl font-semibold">
                  {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.TITLE') }}
                </h2>
                <p class="mt-1 text-sm text-n-slate-10">
                  {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.SUBTITLE') }}
                </p>
              </div>
            </div>
            <div
              class="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4 print:grid-cols-4"
            >
              <article
                class="rounded-xl border border-n-weak bg-n-solid-2/80 p-4 print:bg-white"
              >
                <p class="text-xs text-n-slate-10">
                  {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.AVERAGE_SCORE') }}
                </p>
                <p class="mt-1 text-2xl font-semibold tabular-nums">
                  {{ report.intelligence.score_summary.average_score }}
                </p>
              </article>
              <article
                class="rounded-xl border border-n-ruby-6 bg-n-ruby-3 p-4 print:bg-white"
              >
                <p class="text-xs text-n-ruby-10">
                  {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.AT_RISK_COUNT') }}
                </p>
                <p
                  class="mt-1 text-2xl font-semibold tabular-nums text-n-ruby-12"
                >
                  {{ number(report.intelligence.score_summary.at_risk_count) }}
                </p>
              </article>
              <article
                class="rounded-xl border border-n-teal-6 bg-n-teal-3 p-4 print:bg-white"
              >
                <p class="text-xs text-n-teal-10">
                  {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.HOT_COUNT') }}
                </p>
                <p
                  class="mt-1 text-2xl font-semibold tabular-nums text-n-teal-12"
                >
                  {{ number(report.intelligence.score_summary.hot_count) }}
                </p>
              </article>
              <article
                class="rounded-xl border border-n-amber-6 bg-n-amber-3 p-4 print:bg-white"
              >
                <p class="text-xs text-n-amber-10">
                  {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.OVERDUE_FOLLOWUPS') }}
                </p>
                <p
                  class="mt-1 text-2xl font-semibold tabular-nums text-n-amber-12"
                >
                  {{
                    number(
                      report.intelligence.score_summary.overdue_followups_count
                    )
                  }}
                </p>
              </article>
            </div>
          </div>

          <article class="break-inside-avoid">
            <div class="mb-3">
              <h3 class="text-lg font-semibold">
                {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.NEXT_BEST_ACTIONS') }}
              </h3>
              <p class="mt-1 text-xs text-n-slate-10">
                {{
                  t('COMMERCIAL.REPORTS.INTELLIGENCE.NEXT_BEST_ACTIONS_HINT')
                }}
              </p>
            </div>
            <CommercialIntelligenceTable
              type="actions"
              :rows="report.intelligence.next_best_actions"
              :money="money"
              @action-created="fetchReport"
            />
          </article>
          <article class="break-inside-avoid">
            <div class="mb-3">
              <h3 class="text-lg font-semibold">
                {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.OPPORTUNITIES_AT_RISK') }}
              </h3>
              <p class="mt-1 text-xs text-n-slate-10">
                {{
                  t(
                    'COMMERCIAL.REPORTS.INTELLIGENCE.OPPORTUNITIES_AT_RISK_HINT'
                  )
                }}
              </p>
            </div>
            <CommercialIntelligenceTable
              type="risk"
              :rows="report.intelligence.opportunities_at_risk"
              :money="money"
              @action-created="fetchReport"
            />
          </article>
          <article class="break-inside-avoid">
            <div class="mb-3">
              <h3 class="text-lg font-semibold">
                {{ t('COMMERCIAL.REPORTS.INTELLIGENCE.HOT_OPPORTUNITIES') }}
              </h3>
              <p class="mt-1 text-xs text-n-slate-10">
                {{
                  t('COMMERCIAL.REPORTS.INTELLIGENCE.HOT_OPPORTUNITIES_HINT')
                }}
              </p>
            </div>
            <CommercialIntelligenceTable
              type="hot"
              :rows="report.intelligence.hot_opportunities"
              :money="money"
              @action-created="fetchReport"
            />
          </article>
        </section>

        <section class="grid gap-4 xl:grid-cols-2 print:grid-cols-2">
          <article
            class="break-inside-avoid rounded-2xl border border-n-weak bg-n-solid-2 p-5 shadow-sm print:border-slate-300 print:bg-white print:shadow-none"
          >
            <h3 class="font-semibold">
              {{ t('COMMERCIAL.REPORTS.CHARTS.PIPELINE_VALUE') }}
            </h3>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ t('COMMERCIAL.REPORTS.CHARTS.PIPELINE_VALUE_HINT') }}
            </p>
            <div class="mt-4 h-72">
              <CommercialReportChart :data="pipelineValueChart" />
            </div>
          </article>
          <article
            class="break-inside-avoid rounded-2xl border border-n-weak bg-n-solid-2 p-5 shadow-sm print:border-slate-300 print:bg-white print:shadow-none"
          >
            <h3 class="font-semibold">
              {{ t('COMMERCIAL.REPORTS.CHARTS.PIPELINE_COUNT') }}
            </h3>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ t('COMMERCIAL.REPORTS.CHARTS.PIPELINE_COUNT_HINT') }}
            </p>
            <div class="mt-4 h-72">
              <CommercialReportChart :data="pipelineCountChart" />
            </div>
          </article>
          <article
            class="break-inside-avoid rounded-2xl border border-n-weak bg-n-solid-2 p-5 shadow-sm print:border-slate-300 print:bg-white print:shadow-none"
          >
            <h3 class="font-semibold">
              {{ t('COMMERCIAL.REPORTS.CHARTS.STATUS') }}
            </h3>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ t('COMMERCIAL.REPORTS.CHARTS.STATUS_HINT') }}
            </p>
            <div class="mx-auto mt-4 h-72 max-w-lg">
              <CommercialReportChart type="doughnut" :data="statusChart" />
            </div>
          </article>
          <article
            class="break-inside-avoid rounded-2xl border border-n-weak bg-n-solid-2 p-5 shadow-sm print:border-slate-300 print:bg-white print:shadow-none"
          >
            <h3 class="font-semibold">
              {{ t('COMMERCIAL.REPORTS.CHARTS.LOSSES') }}
            </h3>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ t('COMMERCIAL.REPORTS.CHARTS.LOSSES_HINT') }}
            </p>
            <div v-if="report.losses.by_reason.length" class="mt-4 h-72">
              <CommercialReportChart horizontal :data="lossesChart" />
            </div>
            <div
              v-else
              class="flex h-72 flex-col items-center justify-center gap-2 text-center text-sm text-n-slate-10"
            >
              <span
                class="i-lucide-chart-no-axes-column size-8 text-n-slate-8"
              />{{ t('COMMERCIAL.REPORTS.NO_LOSSES') }}
            </div>
          </article>
        </section>

        <CommercialPipelineTable
          :rows="report.pipeline.stages"
          :money="money"
          :number="number"
        />
        <CommercialReportTable
          :title="t('COMMERCIAL.REPORTS.LOSSES')"
          :headers="[
            t('COMMERCIAL.REPORTS.COLUMNS.REASON'),
            t('COMMERCIAL.REPORTS.COLUMNS.COUNT'),
            t('COMMERCIAL.REPORTS.COLUMNS.VALUE'),
          ]"
          :rows="report.losses.by_reason"
          :cells="
            row => [
              row.name || t('COMMERCIAL.REPORTS.NO_REASON'),
              number(row.count),
              money(row.value),
            ]
          "
        />
      </div>
    </div>
  </main>
</template>
